import sanitizeHtml from 'sanitize-html'

// Rich text (blog posts, task descriptions, notifications, inbound support
// email bodies, ...) is rendered via dangerouslySetInnerHTML. Some of it
// originates outside our own editors — inbound email in particular is
// attacker-reachable — so it must be sanitised before it reaches the DOM.
//
// `class` is deliberately NOT allowed: rich text renders inside app shells where
// the full Bootstrap utility set is compiled, so a single attacker-supplied
// `class="position-fixed top-0 w-100 h-100 bg-body"` builds a full-viewport
// phishing overlay without a line of CSS of its own.
//
// `style` IS allowed but every declaration is filtered by ALLOWED_STYLES below.
// sanitize-html applies NO CSS filtering when `allowedStyles` is unset, which
// lets `position:fixed` (UI redressing) and `background-image:url(...)` (a
// tracking beacon that fires on render) through verbatim.
//
// sanitize-html is pinned to an exact 2.17.5 in package.json and must NOT be
// bumped: 2.17.6 moved its `htmlparser2` dependency from ^10.1.0 to ^12.0.0,
// and htmlparser2@12 is ESM-only (its package `exports` has no `require`
// condition), which Jest's CJS transform cannot load.
const ALLOWED_TAGS = sanitizeHtml.defaults.allowedTags.concat(['img', 'iframe'])

// `iframe` exists to render embedded video, and only that: `src` is constrained below to an
// allowlisted hostname, and no `sandbox`/`allow`/`srcdoc` is in this list, so an attacker cannot
// widen what the embedded document is permitted to do.
const ALLOWED_ATTRIBUTES = {
  ...sanitizeHtml.defaults.allowedAttributes,
  '*': ['style'],
  iframe: ['src', 'width', 'height', 'title', 'allowfullscreen'],
}

// TinyMCE's `media` plugin — the only editor path that produces an `<iframe>` — rewrites every
// youtube.com/watch or youtu.be URL to `https://www.youtube.com/embed/<id>` before it is ever
// saved (`apps/quickscore/admin/public/tinymce/plugins/media/plugin.min.js`'s pattern table, `url:
// "www.youtube.com/embed/$2?$4"`), and the one video actually shipped in product content — the
// QuickScore help-article walkthrough — is seeded at that same host
// (`apps/quickscore/api/src/migration/scripts/2026-08-21-quickscore-help-articles-seed.ts:38,82`,
// `VIDEO_EMBED_SRC = 'https://www.youtube.com/embed/SPHAA6Q3pQg'`). No other host is in use
// anywhere in the repo, so only it is allowed.
const ALLOWED_IFRAME_HOSTNAMES = ['www.youtube.com']

// Typography plus the layout properties our own editor emits. Every value regex
// is anchored and parenthesis-free, so `url(...)`, `var(...)` and
// `expression(...)` can never match, and none of them admits a negative length.
//
// What is deliberately absent is what makes a full-viewport overlay possible:
// `position`, `z-index`, `top`/`left`/`right`/`bottom`, `height` and
// `background-image`. `width` is safe without them — it can widen a block, not
// lift one out of flow — and `vw`/`vh` are not among its accepted units.
const COLOR_VALUES = [
  /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i,
  /^rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d+)\s*)?\)$/i,
  /^[a-z]+$/i,
]

// `auto` and a non-negative length only — a negative margin would drag content
// out from under the app's own chrome.
const MARGIN_VALUE = /^(?:auto|0|\d+(?:\.\d+)?(?:px|%|em|rem))$/i

const ALLOWED_STYLES = {
  '*': {
    color: COLOR_VALUES,
    'background-color': COLOR_VALUES,
    'text-align': [/^(?:left|right|center|justify)$/i],
    'font-size': [/^\d+(?:\.\d+)?(?:px|pt|em|rem|%)$/i],
    'font-weight': [/^(?:normal|bold|bolder|lighter|[1-9]00)$/i],
    'font-style': [/^(?:normal|italic|oblique)$/i],
    'text-decoration': [/^(?:none|underline|overline|line-through)$/i],
    // TinyMCE's own table defaults, `advlist` markers, and the image/table
    // alignment formats — without these, editor-authored tables lose their
    // collapsed borders and width, ordered lists lose their marker style, and
    // aligned images and tables snap back to flush-left.
    'border-collapse': [/^(?:collapse|separate)$/i],
    'list-style-type': [/^[a-z-]+$/i],
    width: [/^\d+(?:\.\d+)?(?:px|%|em|rem)$/i],
    float: [/^(?:left|right|none)$/i],
    'margin-left': [MARGIN_VALUE],
    'margin-right': [MARGIN_VALUE],
  },
}

export const sanitizeRichTextHtml = (contentHtml: string) =>
  sanitizeHtml(contentHtml, {
    allowedTags: ALLOWED_TAGS,
    allowedAttributes: ALLOWED_ATTRIBUTES,
    allowedStyles: ALLOWED_STYLES,
    allowedSchemes: sanitizeHtml.defaults.allowedSchemes,
    allowedIframeHostnames: ALLOWED_IFRAME_HOSTNAMES,
    // sanitize-html defaults this to false as soon as `allowedIframeHostnames` is set (it would
    // otherwise let ANY relative `src` through unchecked, bypassing the hostname allowlist
    // entirely) — set here explicitly so that behaviour doesn't depend on an un-pinned default.
    allowIframeRelativeUrls: false,
  })
